import { Component, OnInit } from "@angular/core";
import {AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponent } from "app/base/base.component";
import { Contract } from "app/core/models/contract";
import { Country } from "app/core/models/country";
import { Merchant } from "app/core/models/merchant";
import { Supplier } from "app/core/models/supplier";
import { ContractService } from "app/core/services/contract.service";
import { MerchantService } from "app/core/services/merchant.service";
import { NotifMessageService } from "app/core/services/notifmessage.service";
import { SupplierService } from "app/core/services/supplier.service";
import { forkJoin, Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { AuthenticatedRouteGuard } from "security/authenticatedRouteGuard.service";

@Component({
  selector: "app-merchant-crud",
  templateUrl: "./merchant-crud.component.html",
})

/**
 * This component handles the display of merchant create, edit and details pages.
 * This is achieved three booleans determining when each user page is displayed.
 * Note: the pages are distinct for the user but handled with the same html template in the code.
 *
 * @implements {OnInit, OnDestroy}
 * @extends {BaseComponent}
 */

export class MerchantCrudComponent extends BaseComponent implements OnInit {


  isCreateMode: boolean;
  isEditMode: boolean;
  isDetailsMode: boolean;

  isLoading: boolean;

  merchantForm: FormGroup;

  supplierSub: Subscription;
  modalResponseSubscription: Subscription;

  merchantInput: Merchant;
  contracts: Array<Contract>;
  suppliers: Array<Supplier> = new Array<Supplier>();

  suppliersId: Array<number> = new Array<number>();

  allSuppliers: Array<Supplier>;
  allSuppliersInit: Array<Supplier>;
  isSaveNeeded: boolean;
  contactFormIsValid: boolean;

  supplierControl: FormControl = new FormControl();
  statusItems: Array<String> = Array<String>();

  paymentMeansList : Array<string> = [];

  merchantId: number;

  countryItems: Array<Country> = Array<Country>();
  contactInfo: {
    address: {
      address_line1: string,
      address_line2: string,
      address_line3: string,
      zip_code: number,
      city: string,
      country: string
    },
    contact: {
      firstName: string,
      lastName: string,
      tel: number,
      email: string
    }
  };
  callbacks: {
    callbackTransactionDataEnabled: boolean,
    callbackTransactionStateEnabled: boolean,
    callbackTransactionStateUrl: string,
    callbackTransactionDataUrl: string,
    callbackSecretKey: string,
  };


  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private merchantService: MerchantService,
    private notifService: NotifMessageService,
    public router: Router,
    private translateService: TranslateService,
    private roleGuard: AuthenticatedRouteGuard,
    private contractService: ContractService,
    private supplierService: SupplierService
  ) {
    super(router);
  }

  get merchant() {
    return this.merchantForm.controls;
  }

  ngOnInit() : void {
    this.countryItems = this.getCountrys();
    this.statusItems = this.getStatus();
    this.paymentMeansList = this.getPaymentMeans();

    this.merchantId = this.route.snapshot.params['id'];
    this.isCreateMode = !this.merchantId;

    if (this.isCreateMode) {
      this.contactFormIsValid = false;
      this.initForm(this.isCreateMode);
    } else {
      if (this.router.url.indexOf("/service/merchant/edit") > -1) {
        this.isEditMode = true
        this.contactFormIsValid = true;
        this.getMerchant(this.merchantId);
        this.isSaveNeeded = this.merchantService.isSaveNeeded = false;
        this.merchantService.merchantSubject.subscribe((isSaveNeededTab) => {
          this.isSaveNeeded = isSaveNeededTab[0];
        });
      } else {
        this.isDetailsMode = true;
        this.getMerchant(this.merchantId);
      }
    }
  }

  initForm(isCreateMode: boolean) : void {
    this.merchantForm = this.formBuilder.group({
      externalRef: new FormControl(
        isCreateMode
        ? ""
        : this.merchantInput.externalRef,
        [Validators.required],
        [this.externalRefValidator()]
      ),
      merchantName: new FormControl(
        isCreateMode
        ? ""
        : this.merchantInput.name,Validators.required
      ),
      acceptFraudLiability: new FormControl(
        isCreateMode
          ? this.fraudLiabilityItems[0]
          : this.merchantInput.acceptFraudLiability,
        Validators.required
      ),
      status: new FormControl(
        isCreateMode ? this.statusItems[0] : this.merchantInput.merchantStatus,
        Validators.required
      ),
      paymentMeans: new FormControl(
        isCreateMode ? "" : this.merchantInput.paymentMeans,
        Validators.required
      ),
    });

    if (isCreateMode) {
      this.merchantForm.addControl("callbackSecretKey", new FormControl(""));
      this.merchantForm.addControl(
        "callbackTransactionDataUrl",
        new FormControl({ value: "", disabled: true })
      );
      this.merchantForm.addControl(
        "callbackTransactionStateUrl",
        new FormControl({ value: "", disabled: true })
      );
      this.merchantForm.addControl(
        "callbackTransactionDataEnabled",
        new FormControl(false)
      );
      this.merchantForm.addControl(
        "callbackTransactionStateEnabled",
        new FormControl(false)
      );
    } else {
      this.merchantForm.addControl(
        "callbackSecretKey",
        new FormControl(
          this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackSecretKey
            : ""
        )
      );
      this.merchantForm.addControl(
        "callbackTransactionDataUrl",
        new FormControl({
          value: this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackTransactionDataUrl
            : "",
          disabled: this.merchantInput.callbacks
            ? !this.merchantInput.callbacks.callbackTransactionDataEnabled
            : false,
        })
      );
      this.merchantForm.addControl(
        "callbackTransactionStateUrl",
        new FormControl({
          value: this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackTransactionStateUrl
            : "",
          disabled:
            this.merchantInput.callbacks && !isCreateMode
              ? !this.merchantInput.callbacks.callbackTransactionStateEnabled
              : false,
        })
      );
      this.merchantForm.addControl(
        "callbackTransactionDataEnabled",
        new FormControl(
          this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackTransactionDataEnabled
            : false
        )
      );
      this.merchantForm.addControl(
        "callbackTransactionStateEnabled",
        new FormControl(
          this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackTransactionStateEnabled
            : false
        )
      );

      const merchantGlobalInfoInit = {
        id: this.merchantInput.id,
        name: this.merchantInput.name,
        externalRef: this.merchantInput.externalRef,
        merchantStatus: this.merchantInput.merchantStatus,
        createdDate: this.merchantInput.createdDate,
        paymentMeans: this.merchantInput.paymentMeans,
        acceptFraudLiability: this.merchantInput.acceptFraudLiability,
        callbacks: {
          callbackSecretKey: this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackSecretKey
            : "",
          callbackTransactionDataUrl: this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackTransactionDataUrl
            : "",
          callbackTransactionStateUrl: this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackTransactionStateUrl
            : "",
          callbackTransactionDataEnabled: this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackTransactionDataEnabled
            : false,
          callbackTransactionStateEnabled: this.merchantInput.callbacks
            ? this.merchantInput.callbacks.callbackTransactionStateEnabled
            : false,
        },
      };
      this.merchantService.merchantGlobalInfo = merchantGlobalInfoInit;

      this.merchantForm.valueChanges.subscribe((val) => {
        let rawValues = this.merchantForm.getRawValue();
        let newMerchantGlobalInfo = {
          id: this.merchantInput.id,
          name: val.merchantName,
          externalRef: this.merchantInput.externalRef,
          merchantStatus: val.status,
          createdDate: this.merchantInput.createdDate,
          paymentMeans: val.paymentMeans,
          acceptFraudLiability: val.acceptFraudLiability,
          callbacks: {
            callbackSecretKey: val.callbackSecretKey,
            callbackTransactionDataUrl: rawValues.callbackTransactionDataUrl,
            callbackTransactionStateUrl: rawValues.callbackTransactionStateUrl,
            callbackTransactionDataEnabled: val.callbackTransactionDataEnabled,
            callbackTransactionStateEnabled:
              val.callbackTransactionStateEnabled,
          },
        };
        this.merchantService.setMerchantGlobalInfo(newMerchantGlobalInfo);
      });
    }
    this.callbackFieldsManagement();
  }

  onSubmitForm() : void {
    this.contactInfo = this.merchantService.getMerchantContactInfo();
    let newMerchant = {
      merchantStatus: this.merchantForm.get("status").value,
      externalRef: this.merchantForm.get("externalRef").value,
      name: this.merchantForm.get("merchantName").value,
      acceptFraudLiability: this.merchantForm.get("acceptFraudLiability").value,
      paymentMeans: this.merchantForm.get("paymentMeans").value,
      supplierIds: [],
      address: {
        address_line1: this.contactInfo.address.address_line1,
        address_line2: this.contactInfo.address.address_line2,
        address_line3: this.contactInfo.address.address_line3,
        zip_code: this.contactInfo.address.zip_code,
        city: this.contactInfo.address.city,
        country: this.contactInfo.address.country,
      },
      contact: {
        firstName: this.contactInfo.contact.firstName,
        lastName: this.contactInfo.contact.lastName,
        tel: this.contactInfo.contact.tel,
        email: this.contactInfo.contact.email,
      },
      callbacks: {
        callbackTransactionDataUrl: this.merchantForm.get(
          "callbackTransactionDataUrl"
        ).value,
        callbackTransactionStateUrl: this.merchantForm.get(
          "callbackTransactionStateUrl"
        ).value,
        callbackTransactionDataEnabled: this.merchantForm.get(
          "callbackTransactionDataEnabled"
        ).value,
        callbackTransactionStateEnabled: this.merchantForm.get(
          "callbackTransactionStateEnabled"
        ).value,
        callbackSecretKey: this.merchantForm.get("callbackSecretKey").value,
      },
    };

    this.merchantService.createMerchant(newMerchant).subscribe((response) => {
      if (response !== null) {
        this.router.navigate(["/service/merchant"]);
        this.notifService.notifySuccess("MERCHANT_CREATED");
      }
    });
  }

  save() : void {
    this.merchantService.updateMerchant().subscribe((res) => {
      if (res != null) {
        this.merchantService.isSaveNeeded = false;
        this.merchantService.merchantSubject.next([
          this.merchantService.isSaveNeeded,
        ]);
        this.notifService.notifySuccess("SAVED");
      }
    });
  }

  getMerchant(id) : void {
    this.isLoading = true;
    this.merchantService.getMerchantById(id).subscribe(
      merchant => {
        this.merchantInput = merchant;
        if (this.isEditMode) {
          this.initForm(this.isCreateMode);
        } else {
          this.manageCallbacks();
        }
        if (this.roleGuard.isEnabled('CONTRACT_READ') && this.merchantId !== undefined) {
          this.getContracts();
        }
      }
    );
  }

  getContracts() : void {
    this.isLoading = true;
    let criteria = {
      merchantId: this.merchantId,
    };
    this.contractService.findByCriteria(criteria).subscribe((contracts) => {
      this.contracts = contracts.content;
      if (this.roleGuard.isEnabled("SUPPLIER_READ")) {
        this.getAllSuppliers().subscribe();
      } else {
        this.isLoading = false;
      }
    });
  }

  getAllSuppliers(): Observable<any> {
    return this.supplierService.getAllSuppliers().pipe(
      map((suppliers) => {
        this.allSuppliers = suppliers.content;
        this.allSuppliersInit = suppliers.content;
        this.supplierControl = new FormControl(this.allSuppliers[0].id);
        this.suppliersId.splice(0, this.suppliersId.length);
        this.suppliers.splice(0, this.suppliers.length);
        if (this.contracts !== undefined && this.contracts.length > 0) {
          this.contracts.forEach((contract) => {
            this.isLoading = true;
            this.suppliersId.push(contract.supplierId);
          });
          this.getSuppliers();
        } else {
          this.isLoading = false;
        }
      })
    );
  }

  getSuppliers() : void {
    this.supplierService
      .getSuppliersById(this.suppliersId)
      .subscribe((suppliers) => {
        this.suppliers = suppliers;
        let listContract: any[] = new Array<any>();
        this.contracts.forEach((element) => {
          listContract.push({
            discountEnabled: element.discountEnabled,
            supplierId: element.supplierId,
          });
        });
        this.suppliers.forEach((supplier) =>
          Object.assign(
            supplier,
            listContract.find((contract) => contract.supplierId == supplier.id)
          )
        );
        this.addSupplier();
      });
  }

  addSupplier() : void {
    let indexTab: Array<number> = [];
    this.allSuppliers = Object.assign([], this.allSuppliersInit);
    this.allSuppliers.forEach((supplier) => {
      if (this.suppliers.some((supp) => supp.id == supplier.id)) {
        indexTab.push(
          this.allSuppliers.findIndex((supp) => supp.id == supplier.id)
        );
      }
    });
    this.allSuppliers = this.allSuppliers.filter(
      (supp, i) => !indexTab.includes(i)
    );
    if (this.allSuppliers.length > 0) {
      this.supplierControl = new FormControl(this.allSuppliers[0].id);
    }
    this.isLoading = false;
  }

  back() : void {
    this.router.navigate(["/service/merchant"]);
  }

  getFraudLiabilityValue(value: boolean): string {
    if (value) {
      return "MERCHANT";
    } else {
      return "ISSUER";
    }
  }

  transSearchFn = (term: string, item: any) => {
    term = term.toLocaleLowerCase();
    return (
      this.translateService.instant(item).toLocaleLowerCase().indexOf(term) > -1
    );
  };

  checkIfContactFormIsValid($event) {
    this.contactFormIsValid = $event;
  }

  callbackFieldsManagement(): void {
    let iscallbackFuelTransactionDataCtrl = this.merchantForm.get(
      "callbackTransactionDataUrl"
    );
    let iscallbackFuelTransactionStateCtrl = this.merchantForm.get(
      "callbackTransactionStateUrl"
    );

    this.merchantForm
      .get("callbackTransactionDataEnabled")
      .valueChanges.subscribe((val) => {
        if (val) {
          iscallbackFuelTransactionDataCtrl.enable();
        } else {
          iscallbackFuelTransactionDataCtrl.disable();
        }
      });

    this.merchantForm
      .get("callbackTransactionStateEnabled")
      .valueChanges.subscribe((val) => {
        if (val) {
          iscallbackFuelTransactionStateCtrl.enable();
        } else {
          iscallbackFuelTransactionStateCtrl.disable();
        }
      });
  }

  toggleEditable(supplierId) : void {
    let contract = this.contracts.find(
      (contrat) => contrat.supplierId == supplierId
    );
    contract.discountEnabled = !contract.discountEnabled;
    this.contractService.updateContract(contract).subscribe((res) => {
      this.notifService.notifySuccess("CONTRACT_UPDATED");
    });
  }

  generateSecretKey() : void {
    this.merchantForm
      .get("callbackSecretKey")
      .setValue(this.getRandomString(15));
  }

  deleteContract(supplierId) : void {
    this.notifService.emitModalWithDetails("DELETE_CONTRACT",["CONTRACT_DELETION","DELETE_CONTRACT"]);

    this.modalResponseSubscription =
      this.notifService.modalResponseSubject$.subscribe((resp) => {
        if (resp) {
          this.contractService
            .deleteContractByMerchantIdAndSupplierId(
              this.merchantId,
              supplierId
            )
            .subscribe((res) => {
              this.getContracts();
              this.notifService.notifySuccess("CONTRACT_DELETED");
            });
        }
        this.modalResponseSubscription.unsubscribe();
      });
  }

  disable(): boolean {
    return !(this.merchantForm.valid && this.contactFormIsValid && this.isSaveNeeded);
  }

  addContract() {
    let newContract = {
      merchantId: this.merchantId,
      supplierId: this.supplierControl.value,
    };
    this.contractService.createContractAPI(newContract).subscribe((res) => {
      if (res != null) {
        this.getContracts();
        this.notifService.notifySuccess("CONTRACT_CREATED");
      }
    });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.isSaveNeeded) {
      this.notifService.emitModal("SAVE_NEEDED");
      return this.notifService.modalResponseSubject$;
    } else {
      return true;
    }
  }

  ngOnDestroy() : void {
    if (this.supplierSub) {
      this.supplierSub.unsubscribe();
    }
  }

  edit(): void {
    this.router.navigate(["/service/merchant/edit", this.merchantId]);
  }

  manageCallbacks() : void {
    this.callbacks = this.merchantInput.callbacks;
    if (!this.callbacks) {
      this.callbacks = {
        callbackTransactionDataEnabled: false,
        callbackTransactionStateEnabled: false,
        callbackTransactionStateUrl: "",
        callbackTransactionDataUrl: "",
        callbackSecretKey: "",
      };
    }
  }

  deleteButton(id) : void {
    if (confirm("Do you really want to delete this supplier?")) {
      this.supplierService.deleteSupplier(id).subscribe();
    }
  }

  /** async validator to check if there exists another merchant with the same external reference. */
  externalRefValidator() : AsyncValidatorFn {
    return (control: AbstractControl) : Observable<ValidationErrors> | null => {
      return this.merchantService.isExternalRefExists(control.value).pipe(
        map(externalRefExists => {
          if (this.merchantInput) {
            return externalRefExists && this.merchantInput.externalRef !== control.value ? {exists:true} : null;
          }
          return externalRefExists ? {exists:true} : null;
        }
      ));
    }
  }

}
